import {
  GET_COLUMNS,
  SET_COLUMN_SORT_INDEX,
  SET_COLUMN_SORT_ORDER,
} from '../../columns/columns.store';
import { GET_GROUPING } from '../../columns/grouping.store';
import { CLEAR_SORTING } from '../../columns/sorting.store';
import { GET_TABLE } from '../../table.ui.store';

import { createElement } from 'src/app/core/helpers/dom';
import { createFooterCellTypeElement } from './utils/create-footer-cell-type-element';
import { createFooterRowElement } from './utils/create-footer-row-element';
import { createFooterTotalPointsElement } from './utils/create-footer-total-points-element';

import { checkElement } from '@core/helpers';
import Tooltip, { destroyTooltipsInSubtree } from 'src/app/project/features/tooltip/Tooltip';
import { TColumn } from '../../columns/column.model';
import { ESortOrder } from '../../sort-order-enum';
import Table from '../table/Table';
import {
  getSelectElementWidth,
  getSelectElementWidthPx,
} from '../table/utils/select-element-width';
import { getVisibleXBounds } from '../utils/get-visible-x-bounds';

export default class TableFooter {
  private table: Table;
  minColumnWidth = 60;
  allSelected = false;
  tooltips: Tooltip[] = [];
  resizeElement: HTMLElement = null;

  totalPointsElement: HTMLElement = null;

  element: HTMLElement = null;
  rowElement: HTMLElement = createFooterRowElement();
  private cellsWrapper: HTMLElement = null;
  private lastReconciledFirstX: number | null = null;
  private lastReconciledLastX: number | null = null;

  constructor(_parent: Table) {
    this.table = _parent;

    this.element = this.create();

    this.table.element.appendChild(checkElement(this.element));
  }

  private create(): HTMLElement {
    return createElement('div', {
      attrs: {
        class: 'table__head table__footer',
      },
      children: [this.rowElement],
    });
  }

  clear(): void {
    this.updateWidth(0);
  }

  load(_keepScrollPosition: boolean = false): void {
    const scrollPosition = _keepScrollPosition ? this.element.scrollLeft : 0;

    this.loadStructure();
    this.table.sortTable();

    this.scrollLeft(scrollPosition);
  }

  loadStructure(): void {
    this.updateWidth(this.table.width);
    this.invalidateReconcileCache();

    const wrapperElement = this.element.children[0];

    this.tooltips.forEach((tooltip) => {
      tooltip.removeTooltip();
    });

    this.tooltips = [];

    if (this.cellsWrapper) {
      destroyTooltipsInSubtree(this.cellsWrapper);
    }

    while (wrapperElement.firstChild) {
      wrapperElement.removeChild(wrapperElement.firstChild);
    }

    wrapperElement.appendChild(checkElement(createFooterTotalPointsElement()));

    this.cellsWrapper = createElement<HTMLElement>('div', {
      attrs: {
        class: 'table__footer__row__cells',
        style: {
          position: 'relative',
          flexShrink: '0',
          height: '100%',
        },
      },
    });
    wrapperElement.appendChild(checkElement(this.cellsWrapper));
  }

  reconcile(): void {
    if (!this.cellsWrapper) {
      return;
    }

    const { firstX, lastX } = getVisibleXBounds(this.table);

    if (firstX === this.lastReconciledFirstX && lastX === this.lastReconciledLastX) {
      return;
    }

    this.lastReconciledFirstX = firstX;
    this.lastReconciledLastX = lastX;

    const visibleXIndexes = this.table.tableBody?.virtualScroller?.visibleXIndexes ?? [];

    while (this.cellsWrapper.firstChild) {
      const child = this.cellsWrapper.firstChild;

      if (child instanceof HTMLElement) {
        destroyTooltipsInSubtree(child);
      }

      this.cellsWrapper.removeChild(child);
    }

    this.cellsWrapper.style.width = `${this.getCellsWrapperWidth()}px`;

    const visibleColumnIndexes = this.table.visibleColumnIndexes ?? [];
    const visibleColumnPositions = this.table.visibleColumnPositions ?? [];
    const columns = GET_COLUMNS();

    visibleXIndexes.forEach((xIndex) => {
      const absoluteIndex = visibleColumnIndexes[xIndex];

      if (absoluteIndex === undefined) {
        return;
      }

      const column = columns[absoluteIndex];

      if (!column || column.hidden) {
        return;
      }

      const left = visibleColumnPositions[xIndex] ?? 0;
      const cellElement = createFooterCellTypeElement(column);

      cellElement.style.position = 'absolute';
      cellElement.style.left = `${left}px`;
      cellElement.style.top = '0';
      cellElement.style.height = '100%';

      this.cellsWrapper.appendChild(checkElement(cellElement));
    });
  }

  private invalidateReconcileCache(): void {
    this.lastReconciledFirstX = null;
    this.lastReconciledLastX = null;
  }

  private getCellsWrapperWidth(): number {
    const columns = GET_COLUMNS();
    const grouping = GET_GROUPING();
    const sum = (this.table.visibleColumnIndexes ?? []).reduce(
      (total, absoluteIndex) => total + (columns[absoluteIndex]?.width ?? 0),
      0,
    );

    return sum + grouping.length * 16;
  }

  updateWidth(_width: number = this.table.width): void {
    const grouping = GET_GROUPING();
    const table = GET_TABLE();
    let footerWidth = _width;

    if (grouping.length) {
      footerWidth = table.width + grouping.length * 16 + getSelectElementWidth(grouping.length);
    }

    (this.element.children[0] as HTMLElement).style.width = `${footerWidth}px`;
  }

  scrollLeft(_left: number): void {
    this.element.scrollLeft = _left;
  }

  addColumn(): void {
    this.invalidateReconcileCache();
    this.reconcile();
  }

  removeColumn(): void {
    this.invalidateReconcileCache();
    this.reconcile();
  }

  moveColumn(): void {
    this.invalidateReconcileCache();
    this.reconcile();
  }

  sortColumn(_column: TColumn): void {
    const sortButton = this.table.siteOptionsService.getSortButton();
    const groupButton = this.table.siteOptionsService.getGroupButton();
    const oldSortOrder = _column.sortOrder;

    CLEAR_SORTING();

    GET_COLUMNS().forEach((__column) => {
      delete __column.sortIndex;
      delete __column.sortOrder;
    });

    let sortOrder = null;

    if (!oldSortOrder) {
      sortOrder = ESortOrder.ASC;
    } else if (oldSortOrder === ESortOrder.ASC) {
      sortOrder = ESortOrder.DESC;
    }

    if (sortOrder) {
      SET_COLUMN_SORT_INDEX(_column.index, 0);
      SET_COLUMN_SORT_ORDER(_column.index, sortOrder);
    } else {
      SET_COLUMN_SORT_INDEX(_column.index);
      SET_COLUMN_SORT_ORDER(_column.index);
    }

    this.table.sortTable({ updatePreferences: true });

    sortButton.update({ closeDropdown: true });
    groupButton.update();
  }

  updateFrozenColumnWidth(): void {
    const selectElement: HTMLElement = document.querySelector('.table__footer__select');
    const grouping = GET_GROUPING();

    if (selectElement) {
      if (grouping.length) {
        selectElement.style.width = getSelectElementWidthPx(grouping.length);
        selectElement.style.marginLeft = '0px';
      } else {
        selectElement.style.width = getSelectElementWidthPx(grouping.length);
        selectElement.style.marginLeft = '0';
      }
    }
  }

  addTooltip(tooltip: Tooltip): void {
    this.tooltips.push(tooltip);
  }

  destroy(): void {
    this.tooltips.forEach((tooltip) => tooltip.removeTooltip());
    this.tooltips = [];
  }
}
